<template>
  <div>
    <h2 class="text-h6">SMTP</h2>
    <h3 class="h4 subtitle mb-4">These SMTP settings will be used to send emails to your clients.</h3>

    <template v-if="!edit">
      <c-button color="primary" class="mb-4" @click="editForm">Edit settings</c-button>
      <template v-if="organisation">
        <table class="table">
          <tr>
            <td>From name</td>
            <td>
              <span v-if="organisation.config.smtp.fromName">{{ organisation.config.smtp.fromName }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.fromName" />
            </td>
          </tr>
          <tr>
            <td>From email</td>
            <td>
              <span v-if="organisation.config.smtp.fromEmail">{{ organisation.config.smtp.fromEmail }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.fromEmail" />
            </td>
          </tr>
          <tr>
            <td>Server</td>
            <td>
              <span v-if="organisation.config.smtp.server">{{ organisation.config.smtp.server }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.server" />
            </td>
          </tr>
          <tr>
            <td>Port</td>
            <td>
              <span v-if="organisation.config.smtp.port">{{ organisation.config.smtp.port }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.port" />
            </td>
          </tr>
          <tr>
            <td>Username</td>
            <td>
              <span v-if="organisation.config.smtp.auth">{{ organisation.config.smtp.auth.username }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.username" />
            </td>
          </tr>
          <tr>
            <td>Secure</td>
            <td>
              <span>{{ organisation.config.smtp.secure | yesNo }}</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.secure" />
            </td>
          </tr>
          <tr>
            <td>TLS: Reject Unauthorized</td>
            <td>
              <span>{{ organisation.config.smtp.tls.rejectUnauthorized | yesNo }}</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.rejectUnauthorized" />
            </td>
          </tr>
        </table>

        <template v-if="!showTestEmail && emailMinimumRequirements()">
          <c-button color="primary" @click="openTestEmail">Send test email</c-button>
        </template>

        <template v-if="showTestEmail">
          <p class="lead">Send a test email</p>

          <c-input v-model="data.body.testEmail" class="input" type="email" placeholder="test@email.com" />

          <c-button-group>
            <c-button color="primary" :disabled="!data.body.testEmail" @click="testEmail">
              <c-spinner v-if="isSending" color="success" size="sm" />
              <span v-else>Send</span>
            </c-button>
            <c-button color="secondary" :disabled="isSending" @click="cancelTestEmail">Cancel</c-button>
          </c-button-group>
        </template>
      </template>
      <template v-else>
        <p class="lead">No SMTP setting have been set.</p>
      </template>
    </template>
    <template v-if="edit">
      <c-form>
        <table class="table">
          <tr>
            <td>From name</td>
            <td>
              <c-input
                :value.sync="$v.data.body.fromName.$model"
                :lazy="false"
                class="input"
                type="text"
                placeholder="Michael Scott"
                :is-valid="checkIfValid('fromName')"
                invalid-feedback="Please enter a from name"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.fromName" />
            </td>
          </tr>
          <tr>
            <td>From email</td>
            <td>
              <c-input
                :value.sync="$v.data.body.fromEmail.$model"
                class="input"
                :lazy="false"
                type="text"
                placeholder="michael@dundermifflin.com"
                :is-valid="checkIfValid('fromEmail')"
                invalid-feedback="Please enter a valid email address"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.fromEmail" />
            </td>
          </tr>
          <tr>
            <td>Server</td>
            <td>
              <c-input
                :value.sync="$v.data.body.server.$model"
                class="input"
                :lazy="false"
                type="text"
                placeholder="smtp.yourserver.com"
                :is-valid="checkIfValid('server')"
                invalid-feedback="Please enter a server address"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.server" />
            </td>
          </tr>
          <tr>
            <td>Port</td>
            <td>
              <c-input
                :value.sync="$v.data.body.port.$model"
                :lazy="false"
                class="input"
                type="text"
                placeholder="456"
                :is-valid="checkIfValid('port')"
                invalid-feedback="Please enter a port number"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.port" />
            </td>
          </tr>
          <tr>
            <td>Username</td>
            <td>
              <c-input
                :value.sync="$v.data.body.username.$model"
                :lazy="false"
                class="input"
                type="text"
                placeholder="username@test.com"
                :is-valid="checkIfValid('username')"
                invalid-feedback="Please enter a username"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.username" />
            </td>
          </tr>
          <tr>
            <td>Password</td>
            <td>
              <c-input
                :value.sync="$v.data.body.password.$model"
                :lazy="false"
                class="input"
                type="password"
                placeholder="SMTP password"
                :is-valid="checkIfValid('password')"
                invalid-feedback="Please enter a password"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.password" />
            </td>
          </tr>
          <tr>
            <td>Secure</td>
            <td>
              <c-select
                :value.sync="$v.data.body.secure.$model"
                :lazy="false"
                :options="yesNoOptions"
                :is-valid="checkIfValid('secure')"
                invalid-feedback="Please select a option"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.secure" />
            </td>
          </tr>
          <tr>
            <td>TLS: Reject Unauthorized</td>
            <td>
              <c-select
                :value.sync="$v.data.body.rejectUnauthorized.$model"
                :lazy="false"
                :options="yesNoOptions"
                :is-valid="checkIfValid('rejectUnauthorized')"
                invalid-feedback="Please select a option"
              />
            </td>
            <td>
              <p v-html="fieldDescriptions.rejectUnauthorized" />
            </td>
          </tr>
        </table>

        <c-button-group>
          <c-button :disabled="!isValid" color="primary" @click="save">
            <c-spinner v-if="isSaving" color="success" size="sm" />
            <span v-else>Update</span>
          </c-button>
          <c-button color="secondary" :disabled="isSaving" @click="cancelEdit">Cancel</c-button>
        </c-button-group>
      </c-form>
    </template>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required, minLength, email, sameAs, helpers, integer,
} from 'vuelidate/lib/validators';
import { buildEndpoint } from '@/libs/buildEndPoint';

export default {
  name: 'SMTPSettings',
  mixins: [validationMixin],
  props: {
    organisation: Object,
  },
  data() {
    return {
      error: null,
      smtp: null,
      edit: false,
      isSaving: false,
      isSending: false,
      showTestEmail: false,
      emailSentOk: false,
      yesNoOptions: [
        {
          disabled: true,
          value: null,
          label: 'Please select one',
        },
        {
          value: 'y',
          label: 'Yes',
        },
        {
          value: 'n',
          label: 'No',
        },
      ],
      data: {
        body: {
          fromName: null,
          fromEmail: null,
          server: null,
          port: null,
          username: null,
          password: null,
          secure: null,
          rejectUnauthorized: null,
          testEmail: null,
        },
      },
      fieldDescriptions: {
        fromName: 'The name that appears in <strong>From</strong> email value',
        fromEmail: 'The email address that the recipient sees',
        server: 'The address of your SMTP server',
        port: 'The port your SMTP server runs on',
        username: 'The username used to authenticate against your SMTP server',
        password: 'The password used to authenticate against your SMTP server',
        secure: 'Does it run under a secure setting?',
        rejectUnauthorized: 'Set this to <strong>No</strong> to ignore invalid security certificates',
      },
    };
  },
  computed: {
    formString() {
      return JSON.stringify(this.data.body, null, 4);
    },
    isValid() {
      return !this.$v.data.body.$invalid;
    },
    isDirty() {
      return this.$v.data.body.$anyDirty;
    },
  },
  mounted() {
    this.setData();
  },
  validations: {
    data: {
      body: {
        fromName: {
          required,
        },
        fromEmail: {
          required,
          email,
        },
        server: {
          required,
        },
        port: {
          required,
          integer,
        },
        username: {
          required,
        },
        password: {
          required,
        },
        secure: {
          required,
        },
        rejectUnauthorized: {
          required,
        },
      },
    },
  },
  methods: {
    editForm() {
      this.edit = true;
    },
    cancelEdit() {
      this.edit = false;
      this.setData();
    },
    openTestEmail() {
      this.emailSentOk = false;
      this.showTestEmail = true;
    },
    cancelTestEmail() {
      this.showTestEmail = false;
    },
    testEmail() {
      if (!this.data.body.testEmail) {
        return;
      }
      const data = {
        id: this.organisation._id,
        toEmail: this.data.body.testEmail,
      };
      this.isSending = true;
      this.$http
        .post(buildEndpoint('organisation-admin/organisation/smtp/test'), data)
        .then((response) => {
          this.isSending = false;
          if (response.data.success) {
            this.emailSentOk = true;
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message:
                'Hey, it looks like the email was sent successfully! Please check the inbox of the test email you sent it to.',
            });
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error sending test email',
              message: response.data.error,
            });
          }
        })
        .catch((error) => {
          this.isSending = false;
          this.error = error;
          this.$store.commit('addErrorMessage', {
            header: 'Error sending test email',
            message: error,
          });
        });
    },
    checkIfValid(fieldName) {
      const field = this.$v.data.body[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },

    emailMinimumRequirements() {
      return (
        _.get(this.organisation, 'config.smtp.server')
        && _.get(this.organisation, 'config.smtp.fromName')
        && _.get(this.organisation, 'config.smtp.fromEmail')
        && _.get(this.organisation, 'config.smtp.auth.username')
        && _.get(this.organisation, 'config.smtp.auth.password')
      );
    },
    setData() {
      if (_.has(this.organisation, 'config.smtp')) {
        this.smtp = this.organisation.config.smtp;
      }
      const secure = _.get(this.organisation, 'config.smtp.secure', false);
      const rejectUnauthorized = _.get(this.organisation, 'config.smtp.tls.rejectUnauthorized', false);
      this.data.body.fromName = _.get(this.organisation, 'config.smtp.fromName', '');
      this.data.body.fromEmail = _.get(this.organisation, 'config.smtp.fromEmail', '');
      this.data.body.server = _.get(this.organisation, 'config.smtp.server', '');
      this.data.body.port = _.get(this.organisation, 'config.smtp.port', '');
      this.data.body.username = _.get(this.organisation, 'config.smtp.auth.username', '');
      this.data.body.password = _.get(this.organisation, 'config.smtp.auth.password', '');
      this.data.body.secure = secure ? 'y' : 'n';
      this.data.body.rejectUnauthorized = rejectUnauthorized ? 'y' : 'n';
      this.$v.$reset();
    },
    save() {
      this.$v.$touch();

      if (!this.isValid) {
        return;
      }

      this.isSaving = true;
      this.error = null;

      const data = {
        id: this.organisation._id,
        server: this.data.body.server,
        fromName: this.data.body.fromName,
        fromEmail: this.data.body.fromEmail,
        username: this.data.body.username,
        password: this.data.body.password,
        port: this.data.body.port,
        secure: this.data.body.secure === 'y',
        rejectUnauthorized: this.data.body.rejectUnauthorized === 'y',
      };

      this.$http
        .post(buildEndpoint('organisation-admin/organisation/smtp/update'), data)
        .then((response) => {
          this.isSaving = false;
          if (response.data.success) {
            if (!this.organisation.config.smtp.auth) {
              this.organisation.config.smtp.auth = {};
            }
            if (!this.organisation.config.smtp.tls) {
              this.organisation.config.smtp.tls = {};
            }
            // Update data in read only view
            this.organisation.config.smtp.fromName = data.fromName;
            this.organisation.config.smtp.fromEmail = data.fromEmail;
            this.organisation.config.smtp.server = data.server;
            this.organisation.config.smtp.auth.username = data.username;
            this.organisation.config.smtp.auth.password = data.password;
            this.organisation.config.smtp.auth.port = data.port;
            this.organisation.config.smtp.secure = data.secure;
            this.organisation.config.smtp.tls.rejectUnauthorized = data.rejectUnauthorized;
            this.edit = false;
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message: 'SMTP settings updated',
            });
            this.$v.$reset();
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'There was a problem saving SMTP settings',
            });
          }
        })
        .catch((error) => {
          this.isSaving = false;
          this.error = error;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: 'There was a problem saving SMTP settings',
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped="">
table {
  tr {
    td {
      &:first-child {
        font-weight: bold;
        width: 200px;
      }
      &:last-child {
        font-style: italic;
      }
      .not-set {
        color: silver;
      }
    }
  }
}
.buttons {
  margin-bottom: 20px;
}
.info {
  font-weight: normal;
  font-style: italic;
  margin-bottom: 20px;
}
</style>
